/**
 * Canonical form for comparing address fragments: the same house number is
 * stored as "3к1" by the geocoder and "3, к. 1" by the form.
 */
const canonical = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[\s.,/-]+/g, '')
    .replace(/ё/g, 'е');

const stripCity = (line: string, city?: string | null): string => {
  const cityAffix = city?.trim();
  if (!cityAffix || !line.toLowerCase().startsWith(cityAffix.toLowerCase())) {
    return line;
  }
  const rest = line.slice(cityAffix.length).trimStart();
  return rest.startsWith(',') ? rest.slice(1).trimStart() : line;
};

export interface AddressLineParts {
  /** Street without the city prefix and the house number. */
  street: string;
  /** House number, exactly as the record carries it. */
  building: string;
}

/**
 * Splits a saved address line ("{city}, {street}, {building}") into the parts
 * the LMS payload carries separately.
 *
 * Deliberately deterministic: the trailing segment is dropped only when it IS
 * the record's own building (equal in canonical form). A line whose house
 * number cannot be matched this way is sent as it is — guessing what part of a
 * free-form address is the house is the backend geocoder's job, not a frontend
 * word list's.
 */
export const splitAddressLine = (
  addressLine: string,
  city?: string | null,
  building?: string | null,
): AddressLineParts => {
  const street = stripCity(addressLine.trim(), city);
  const knownBuilding = building?.trim() ?? '';

  const segments = street.split(',');
  const lastSegment = segments[segments.length - 1]?.trim() ?? '';

  if (
    knownBuilding &&
    segments.length > 1 &&
    canonical(lastSegment) === canonical(knownBuilding)
  ) {
    return {
      street: segments.slice(0, -1).join(',').trim(),
      building: knownBuilding,
    };
  }

  return { street: street || addressLine.trim(), building: knownBuilding };
};
